import { Stack, Autocomplete, TextField } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import type { SelectFieldProps, SelectOption } from '@shared/components/inputs/SelectField/SelectField.types';

const SelectField = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  helperText,
  isDisabled,
  isMulti,
  isSearchable = false,
  inputHeight = 41,
  maxMenuHeight,
  menuPlacement = 'auto',
}: SelectFieldProps) => {
  const defaultValue = isMulti ? [] : null;
  const resolvedValue = value ?? defaultValue;
  const popperPlacement = menuPlacement === 'top' ? 'top-start' : 'bottom-start';
  const showPlaceholder = isMulti || !value;

  const handleChange = (_event: React.SyntheticEvent, newValue: SelectOption | SelectOption[] | null) => {
    if (isMulti) {
      (onChange as (v: SelectOption[]) => void)(newValue as SelectOption[]);
    } else {
      (onChange as (v: SelectOption | null) => void)(newValue as SelectOption | null);
    }
  };

  return (
    <Stack gap="2px" width="100%">
      <Autocomplete
        multiple={!!isMulti}
        options={options}
        value={resolvedValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={isDisabled}
        disableClearable={!isMulti}
        openOnFocus
        isOptionEqualToValue={(option, val) => option.value === val.value}
        popupIcon={<ArrowDropDownRoundedIcon />}
        filterOptions={!isSearchable ? (opts) => opts : undefined}
        ListboxProps={maxMenuHeight ? { sx: { maxHeight: maxMenuHeight } } : undefined}
        componentsProps={{
          popper: {
            placement: popperPlacement,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={showPlaceholder ? placeholder : undefined}
            error={!!error}
            helperText={helperText || ''}
            FormHelperTextProps={{
              component: FormErrorMessage,
            }}
            inputProps={{
              ...params.inputProps,
              readOnly: !isSearchable,
            }}
          />
        )}
        sx={{
          ...(inputHeight !== 41 && {
            '& .MuiOutlinedInput-root': {
              minHeight: `${inputHeight}px`,
            },
          }),
          ...(!isSearchable && {
            '& .MuiAutocomplete-input': {
              cursor: 'pointer',
            },
          }),
        }}
      />
    </Stack>
  );
};

export default SelectField;
